import { observer } from "mobx-react-lite"
import { useEffect, useRef } from "react"
import { useStore } from "../../contexts/store"
import dayjs from "dayjs"

import { Rect as RectModel } from "../../../../shared/src/types/Interaction"
import { Op_addAnnotation, Op_editAnnotation } from "../../shared/src/types/Ops"
import { interactionAnchor } from "../../shared/src/types/Interaction"
import { SelectionRange } from "../../stores/uiStore"
import { getSvgFromCoordinates } from "./AnnotationInProgress"
import { getCaretRangeAtXY, getPageFromCoordinate, getPdfSpans, mergeAnchors, optimizeRects, rectIntersect } from "../../helper/pdfClickEvents"
import { SelectionTool } from "../../shared/src/types/Session"

import { alert } from '../../stores/alertStore'
import { useTranslation } from "react-i18next"

const longPressDuration = 600
const contiguousAnnotationDelay = 1500
const maxClickDuration = 200
const minimalMarkingLength = 2
const countdownSteps = 50


export function parseToFixed(number: number) {
  return parseFloat(number.toFixed(3))
}

export const rectsFromRange = (range:Range, scale:number, includePageRectOffset:boolean = false) => {
  var rects:RectModel[]|null = null
  const walkerFilter = (node:Node) => {
    if ((node.nodeName === 'SPAN') && (node as Element).getAttribute('role') === 'presentation' ) return NodeFilter.FILTER_ACCEPT
    return NodeFilter.FILTER_SKIP
  }
  const walker = document.createTreeWalker(range.commonAncestorContainer, NodeFilter.SHOW_ELEMENT, { acceptNode: walkerFilter })
  var elm:Node|null = null

  const normalizeRect = (pE:Element, scale:number, domRect:DOMRect, pageNumber:number) => {
    const pageRect = pE.getBoundingClientRect()
    // transform domRect coordinates into page coordinates
    var x = domRect.x - pageRect.x
    x = parseToFixed(x / scale)
    var y = domRect.y - pageRect.y
    y = parseToFixed(y / scale)
    // get values from domRect
    const h = domRect.height / scale
    const w = domRect.width / scale
    // check if marking is inside page boundaries and has min values
    if(x > 0 && (x+w) < (pageRect.width/scale) && y > 0 && (y+h) < (pageRect.height/scale) && h > 1 && w > 1 && !Number.isNaN(pageNumber)) {
      const rect: RectModel = {
        x: parseToFixed(x) + (includePageRectOffset ? (pageRect.left / scale) : 0),
        y: parseToFixed(y) + (includePageRectOffset ? (pageRect.top / scale) : 0),
        h: parseToFixed(h),
        w: parseToFixed(w),
        p: pageNumber
      }
      return rect
    }
    return null
  }

  const pageElement = (o:Element) => {
    const pageElement = o.closest('.react-pdf__Page')
    if (!pageElement) { console.error(`Missing pageElement`); return null }
    return pageElement
  }

  const pageNumber = (pageElement:Element) => {
    const pageNumber = parseInt(pageElement.getAttribute('data-page-number')||'')
    if (!pageNumber) { console.error(`Missing pageNumber`); return null }
    return pageNumber
  }

  const partial = (node:Node, start:number, end:number) => {
    const pE = pageElement(node as HTMLElement)
    if (!pE) return []
    const pN = pageNumber(pE)
    if (!pN) return []

    const newRange = new Range()
    const textNode = node.firstChild

    if (textNode) {
      newRange.setStart(textNode, start)
      newRange.setEnd(textNode, end)
      return [...newRange.getClientRects()].map(r => normalizeRect(pE, scale, r, pN)).filter(r => r!==null)
    }
    return []
  }

  if (range.commonAncestorContainer === range.startContainer && range.commonAncestorContainer === range.endContainer) {
    if (range.commonAncestorContainer.parentNode) {
      const r = partial(range.commonAncestorContainer.parentNode, range.startOffset, range.endOffset)
      if (r && r.length) rects = r
    }
    return rects || []
  }


  while((elm = walker.nextNode())) {
    const o:HTMLElement = elm as HTMLElement

    if (elm === range.startContainer.parentNode) {
      const r = partial(elm, range.startOffset, elm.textContent?.length || 0)
      if (r) rects = r
      continue
    }

    if (rects !== null) {

      if (elm === range.endContainer.parentNode) {
        const r = partial(elm, 0, range.endOffset)
        if (r && r.length) rects.push(...r)
        return rects || []
      }

      const pE = pageElement(o)
      if (!pE) continue
      const pN = pageNumber(pE)
      if (!pN) continue
      const list = [...o.getClientRects()].map(r => normalizeRect(pE, scale, r, pN)).filter(r => r!==null)
      rects.push(...list)
    }
  }

  return []
}

export const getSelectionRange = (r1:SelectionRange, r2:SelectionRange) => {
  const range = new Range()
  const domOrder = r1.node.compareDocumentPosition(r2.node)
  if ((domOrder === 4) || ((domOrder === 0) && (r1.offset < r2.offset))) {
    range.setStart(r1.node, r1.offset)
    range.setEnd(r2.node, r2.offset)
  }
  else if ((domOrder === 2) || ((domOrder === 0) && (r1.offset > r2.offset))) {
    range.setEnd(r1.node, r1.offset)
    range.setStart(r2.node, r2.offset)
  }
  else {
    // console.error('range order unclear')
  }
  return range
}




const PdfClickEvents = ({interactionEditId, nodeId, podId, storeId}: {interactionEditId?: string, nodeId: string, podId: string, storeId: string}) => {
  const { pdfMetaStore, podStore, uiStore, opStore, sessionStore, alertStore } = useStore()
  const { t } = useTranslation()

  const { session } = sessionStore
  const pointerDeviceRef: any = useRef(null)

  var longPressTimerRef = useRef<NodeJS.Timeout>(null)
  var contiguousAnnotationIntervalRef = useRef<NodeJS.Timeout>(null)
  var clickCoordinates = useRef<{x:number,y:number,t:number}>(null)
  var noScrollingElement = useRef<HTMLElement>(null)
  var lastRecentlyAdded = useRef<{
    tCreated:number,
    interactionId:string|boolean,
  }>(null)

  const pod = podStore.pod
  var tool:SelectionTool|null = sessionStore.getSelectionTool()
  //if (!tool) return null
  //if (!session) return null

  const saveMarkerAnnotation = () => {
    tool = sessionStore.getSelectionTool()
    if (!tool) return
    if (!session) return
    if ((uiStore.selectionStartRange) && (uiStore.selectionEndRange)) {
      const range = getSelectionRange(uiStore.selectionStartRange, uiStore.selectionEndRange)
      if (range) {
        const text = range.toString()
        const rects: RectModel[] = rectsFromRange(range, pdfMetaStore.getScale(storeId))

        if(rects.length) {

          const color = tool.color
          const optimizedRects = optimizeRects(rects)

          if (optimizedRects.length) {
            const anchor: interactionAnchor = {
              nodeId: nodeId,
              relText: text,
              rects: optimizedRects,
              tool: 'marker'
            }

            if(interactionEditId) {
              if(interactionEditId === "addLink") {
                uiStore.setSelectedLinkAnchor(anchor)
                return true
              } else {
                podStore.setInteractionEditAnchor(interactionEditId, anchor)
                return true
              }
            }
            // save selected rects as annotation
            else {
              const op:Op_addAnnotation = {
                op: 'addAnnotation',
                podId: podId,
                data: {
                  usergroupId: podStore.getUsergroupByRole('Private').usergroupId,
                  interactionType: "annotation",
                  interactionId: sessionStore.createUuid(),
                  userId: session.user.userId,
                  userName: podStore.userPseudonym,
                  anchor: anchor,
                  style: { color },
                  label: "",
                  reactions: {},
                  tCreated: dayjs().unix(),
                  tModified: dayjs().unix(),
                  coid: null,
                }
              }
              opStore.doOp(op)
              return op.data.interactionId
            }
          }
        }
      }
    }
    return false
  }

  const saveFreehandAnnotation = () => {
    tool = sessionStore.getSelectionTool()
    if (!tool) return
    if (!session) return
    const scale = pdfMetaStore.getScale(storeId)
    const color = tool.color
    const isContiguous = Boolean(lastRecentlyAdded.current)
    var xMin:number = 0, yMin:number = 0,
        xMax:number = 0, yMax:number = 0,
        pageNumber:number = 0,
        strokeSvg = ''

    if ((tool.type === 'pencil') || (tool.type === 'highlighter')) {
      pageNumber = uiStore.pencilData.page || 0
    }
    else if ((tool.type=== 'box')) {
      pageNumber = uiStore.annotationInProgressData?.startP || 0
    }

    if (pageNumber === 0) { console.error('No valid page number in saveFreehandAnnotation()'); return false }

    const pageElement = document.getElementById(`${storeId}-page-${pageNumber-1}`)?.children[1]?.children[1]
    const pageRect = pageElement?.getBoundingClientRect()
    if (!pageRect) { console.error('No valid pageRect in saveFreehandAnnotation()'); return false }

    if (uiStore.pencilData.page && uiStore.pencilData.coordinates.length && ((tool.type === 'pencil') || (tool.type === 'highlighter'))) {
      const pencilCoordinates = uiStore.pencilData.coordinates
      if (pageRect) {
        const padding = (tool.radius || 5) * scale
        xMin = Math.min(...pencilCoordinates.map(p => p.x)) - pageRect.x - padding
        yMin = Math.min(...pencilCoordinates.map(p => p.y)) - pageRect.y - padding
        xMax = Math.max(...pencilCoordinates.map(p => p.x)) - pageRect.x + padding
        yMax = Math.max(...pencilCoordinates.map(p => p.y)) - pageRect.y + padding
        pageNumber = uiStore.pencilData.page
        const coordinates = [...uiStore.pencilData.coordinates].map(p => ({ x: (p.x - xMin - pageRect.x)/scale, y: (p.y - yMin - pageRect.y)/scale, t: p.t }))
        strokeSvg = getSvgFromCoordinates(coordinates, 1, tool)
      }
    }
    else if (uiStore.annotationInProgressData) {
      xMin = Math.min(uiStore.annotationInProgressData.startX, uiStore.annotationInProgressData.endX) - pageRect.x
      yMin = Math.min(uiStore.annotationInProgressData.startY, uiStore.annotationInProgressData.endY) - pageRect.y
      xMax = Math.max(uiStore.annotationInProgressData.startX, uiStore.annotationInProgressData.endX) - pageRect.x
      yMax = Math.max(uiStore.annotationInProgressData.endY, uiStore.annotationInProgressData.startY) - pageRect.y
      pageNumber = uiStore.annotationInProgressData.startP
    }

    const rect:RectModel = {
      x: parseToFixed(xMin / scale),
      y: parseToFixed(yMin / scale),
      w: parseToFixed((xMax - xMin) / scale),
      h: parseToFixed((yMax - yMin) / scale),
      p: pageNumber
    }

    switch(tool.type) {
      case 'box':
        if (!isContiguous && ((rect.w<minimalMarkingLength) || (rect.h<minimalMarkingLength))) return false
        break
      case 'pencil':
      case 'highlighter':
        if (!isContiguous && (uiStore.pencilData.coordinates.length<minimalMarkingLength)) return false
    }

    if (strokeSvg) rect.svg = strokeSvg

    const anchor:interactionAnchor = {
      nodeId: nodeId,
      relText: '',
      rects:[rect],
      tool: tool.type
    }


    // extract anchor relText from page nodes
    if (pageElement && pageRect) {
      const r1 = {
        left: pageRect.left + xMin,
        top: pageRect.top + yMin,
        width: xMax-xMin,
        height: yMax-yMin
      }

      for(var elm of getPdfSpans(pageElement)){
        if (elm && elm.getBoundingClientRect) {
          const spanrect = elm.getBoundingClientRect()
          const r2 = {
            left: spanrect.left,
            top: spanrect.top,
            width: spanrect.width,
            height: spanrect.height
          }
          const intersect = rectIntersect(r1, r2)
          if (intersect.squarepix) {

            if (elm.textContent) for(var i=0; i<elm.textContent.length; i++) {
              const range = document.createRange()
              range.setStart(elm.childNodes[0], i)
              range.setEnd(elm.childNodes[0], i+1)
              const r3 = range.getBoundingClientRect()
              const i2 = rectIntersect(r1, r3)
              const char = i2.squarepix ?  elm.textContent.substring(i, i+1) : '+'

              // include all letters that are mostly inside the marker
              if (i2.squarepix > (r3.width * r3.height)*.9) anchor.relText = anchor.relText + char
            }
            anchor.relText = anchor.relText + ` `
          }
        }
      }
    }

    // if interactionEditId: save selection for edit Viewer or adding a link
    if(interactionEditId) {
      if(interactionEditId === "addLink") {
        uiStore.setSelectedLinkAnchor(anchor)
        return true
      } else {
        if (isContiguous) {
          const anchorOld = podStore.getInteractionEditAnchor(interactionEditId)
          if (anchorOld) podStore.setInteractionEditAnchor(interactionEditId, mergeAnchors(anchorOld, anchor))
        }
        else {
          podStore.setInteractionEditAnchor(interactionEditId, anchor)
        }
        return true
      }
    }
    // else save selected rects as operation
    else {
      if (lastRecentlyAdded.current && typeof lastRecentlyAdded.current.interactionId === 'string') {
        const interaction = podStore.pod?.getInteraction(lastRecentlyAdded.current.interactionId)
        if (interaction) {
          const op:Op_editAnnotation   = {
            op: 'editAnnotation',
            podId: podId,
            data: {
              usergroupId: podStore.getUsergroupByRole('Private').usergroupId,
              interactionId: lastRecentlyAdded.current.interactionId,
              userId: session.user.userId,
              userName: podStore.userPseudonym||'',
              mods: {
                anchor: mergeAnchors(interaction.anchor, anchor),
              },
            }
          }
          opStore.doOp(JSON.parse(JSON.stringify(op)))
          return op.data.interactionId
        }
      }
      else {
        const interactionId = sessionStore.createUuid()
        const op:Op_addAnnotation = {
          op: 'addAnnotation',
          podId: podId,
          data: {
            usergroupId: podStore.getUsergroupByRole('Private').usergroupId,
            interactionType: 'annotation',
            interactionId,
            userId: session.user.userId,
            userName: podStore.userPseudonym,
            anchor: anchor,
            style: { color },
            label: "",
            reactions: {},
            tCreated: dayjs().unix(),
            tModified: dayjs().unix(),
            coid: null,
          }
        }
        opStore.doOp(op)
        return op.data.interactionId
      }
    }
    return false
  }

  const click = () => {
    if ((clickCoordinates.current) && (Date.now() - clickCoordinates.current.t < maxClickDuration)) {
      const x = clickCoordinates.current.x
      const y = clickCoordinates.current.y
      if (!interactionEditId) selectInteraction(x, y)
    }
    clickCoordinates.current = null
  }

  const currentToolAllowed = () => {
    const tool = sessionStore.getSelectionTool()
    if (interactionEditId && pod && tool) {
      const interaction = pod.getInteraction(interactionEditId)
      if (interaction?.usergroupId === podStore.getUsergroupByRole('Private').usergroupId) return true                // private interactions can be done in any tool
      if ((tool.type === 'pencil') && (pod.permissions['sharePencilInteraction'])) return true                        // allow using the pencil tool for the remaining (non-private) interactions if the permission applies
      if ((tool.type === 'highlighter') && (pod.permissions['shareHighlighterInteraction'])) return true              // allow using the highlighter tool for the remaining (non-private) interactions if the permission applies
      if ((tool.type === 'box') && (pod.permissions['shareBoxInteraction'])) return true                              // allow using the box tool for the remaining (non-private) interactions if the permission applies
      if (tool.type === 'marker')  return true                                                                        // always allow the regular inline marker
      alertStore.push(alert(t('You cannot use the current tool for non-private interactions. Please choose a different one.'), 'warning'))
      return false
    }
    return true
  }

  const selectInteraction = (clickX: number, clickY: number) => {
    // determine selected page
    const page = getPageFromCoordinate(storeId, clickX, clickY)
    if(page) {
      const pageNumber = page.pageNumber
      const pageRect = page.pageRect
      const currentScale = pdfMetaStore.getScale(storeId)
      // x position in relation to page element
      let x = clickX - pageRect.x
      // normalized x position
      x = parseToFixed(x / currentScale)
      // y position in relation to page element
      let y = clickY - pageRect.y
      // normalized y position
      y = parseToFixed(y / currentScale)

      const selectedInteractions = podStore.pod?.getInteractionsByCoordinates(nodeId, x, y, pageNumber, currentScale, storeId)
      if(selectedInteractions){
        // remove doubles from selected interactions
        const selectedInteractionIds: Array<string> = []
        const uniqueSelectedInteractions = []
        for(const selected of selectedInteractions) {
          const interactionId = selected.interaction.interactionId
          if(interactionId && !selectedInteractionIds.includes(interactionId)) {
            uniqueSelectedInteractions.push(selected)
            selectedInteractionIds.push(interactionId)
          }
        }
        // open interaction or interaction swiper
        if(uniqueSelectedInteractions.length === 1) uiStore.selectInteraction(uniqueSelectedInteractions[0], podId, nodeId)
        else uiStore.openOverlappingInteractionMenu(uniqueSelectedInteractions)
      }
    }
  }

  useEffect(() => {
    // eventListener controller
    const controller = new AbortController()
    const { signal } = controller

    const startDetectLongPress = (e:TouchEvent) => {
      if (e.touches.length === 1) {
        if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current)
        longPressTimerRef.current = setTimeout(() => { longPress(e) }, longPressDuration)
      }
    }

    const longPress = (e:TouchEvent) => {
      if (e.touches.length === 1) startAnnotating(e.touches[0].clientX, e.touches[0].clientY, e.touches[0].force)
    }

    const startAnnotating = (x:number, y:number, force?:number) => {
      const pageInfo = getPageFromCoordinate(storeId, x, y)
      if (!pageInfo) return false

      if ((x < pageInfo.pageRect.x) || (x>pageInfo.pageRect.right)) return false
      if ((y < pageInfo.pageRect.y) || (y>pageInfo.pageRect.bottom)) return false

      if (contiguousAnnotationIntervalRef.current) {
        clearInterval(contiguousAnnotationIntervalRef.current)
        contiguousAnnotationIntervalRef.current = null
        uiStore.setContiguousCountdown(100)
      }
      uiStore.setAnnotationInProgress(true)

      if (pageInfo.pageElement.parentNode?.parentNode?.parentElement?.style) {
        noScrollingElement.current = pageInfo.pageElement.parentNode.parentNode.parentElement
        noScrollingElement.current.style.overflow = 'hidden'
      }

      switch(tool?.type) {
        case 'marker':
          const range = getCaretRangeAtXY(x, y)
          if (range) uiStore.setSelectionStartRange(range)
          break
        case 'box':
          uiStore.setAnnotationInProgressData({
            startX: Math.max(pageInfo.pageRect.x, Math.min(pageInfo.pageRect.right, x)),
            startY: Math.max(pageInfo.pageRect.y, Math.min(pageInfo.pageRect.bottom, y)),
            startP: pageInfo.pageNumber,
            endX: Math.max(pageInfo.pageRect.x, Math.min(pageInfo.pageRect.right, x)),
            endY: Math.max(pageInfo.pageRect.y, Math.min(pageInfo.pageRect.bottom, y)),
            endP: pageInfo.pageNumber,
          })
          break

        case 'pencil':
        case 'highlighter':
          uiStore.setPencilData(pageInfo.pageNumber, [{
            x: Math.max(pageInfo.pageRect.x, Math.min(pageInfo.pageRect.right, x)),
            y: Math.max(pageInfo.pageRect.y, Math.min(pageInfo.pageRect.bottom, y)),
            t: (pointerDeviceRef.current === 'pen') ? (force || 1) : Date.now()
          }])
          break
      }
    }

    const endAnnotating = (x?:number, y?:number) => {
      uiStore.setAnnotationInProgress(false)
      if (contiguousAnnotationIntervalRef.current) clearInterval(contiguousAnnotationIntervalRef.current)

      if (noScrollingElement.current) {
        noScrollingElement.current.style.overflow = 'auto'
        noScrollingElement.current = null
      }

      if (x && y) {
        const interactionId = (tool?.type==='marker') ? saveMarkerAnnotation() : saveFreehandAnnotation()
        if (!interactionId) {
          click()
        }
      }

      lastRecentlyAdded.current = null
    }

    const deferredEndAnnotating = () => {
      if (contiguousAnnotationIntervalRef.current) clearInterval(contiguousAnnotationIntervalRef.current)
      uiStore.setContiguousCountdown(100)
      contiguousAnnotationIntervalRef.current = setInterval(() => {
        uiStore.setContiguousCountdown(uiStore.contiguousCountdown - (100/countdownSteps))
        if (uiStore.contiguousCountdown <= 0) endAnnotating()
      }, contiguousAnnotationDelay/countdownSteps)
    }

    const trackAnnotation = (x:number, y:number, force:number|null=null) => {
      const pageInfo = getPageFromCoordinate(storeId, x, y)
      if (!pageInfo) return false

      switch(tool?.type) {
        case 'marker':
          const range = getCaretRangeAtXY(x, y)
          if (range) uiStore.setSelectionEndRange(range)
          break
        case 'box':
          if ((uiStore.annotationInProgressData) && (uiStore.annotationInProgressData.startP === pageInfo.pageNumber)) uiStore.setAnnotationInProgressData({
            startX: uiStore.annotationInProgressData.startX,
            startY: uiStore.annotationInProgressData.startY,
            startP: uiStore.annotationInProgressData.startP,
            endX: Math.max(pageInfo.pageRect.x, Math.min(pageInfo.pageRect.right, x)),
            endY: Math.max(pageInfo.pageRect.y, Math.min(pageInfo.pageRect.bottom, y)),
            endP: pageInfo.pageNumber,
          })
          break

        case 'pencil':
        case 'highlighter':
          if (uiStore.pencilData.page && pageInfo.pageNumber === uiStore.pencilData.page) uiStore.addPencilPoint({
            x: Math.min(Math.max(x, pageInfo.pageRect.left), pageInfo.pageRect.right),
            y: Math.min(Math.max(y, pageInfo.pageRect.top), pageInfo.pageRect.bottom),
            t: (pointerDeviceRef.current === 'pen') ? (force || 1) : Date.now()
          })
          break
      }
    }

    //// event handler
    const handleMouseDown = (e:MouseEvent) => {
      tool = sessionStore.getSelectionTool()
      if (!currentToolAllowed()) return false
      const selection = document.getSelection()
      selection?.removeAllRanges()
      if (uiStore.annotationInProgress) {
        if (!startAnnotating(e.clientX, e.clientY)) { e.stopPropagation(); e.preventDefault(); e.stopImmediatePropagation() }
      }
      else {
        clickCoordinates.current = { x: e.clientX, y: e.clientY, t:Date.now() }
      }
    }

    const handleMouseMove = (e:MouseEvent) => {
      if (e.buttons === 1) {
        if (clickCoordinates.current) {
          if (!uiStore.annotationInProgress) startAnnotating(clickCoordinates.current.x, clickCoordinates.current.y)
        }
        trackAnnotation(e.clientX, e.clientY)
      }
    }

    const handleClick = (e:TouchEvent|MouseEvent|{clientX:number, clientY:number}) => {
      tool = sessionStore.getSelectionTool()
      if (!currentToolAllowed()) return false
      const clientX = Math.floor((e instanceof MouseEvent) ? e.clientX : ((e instanceof TouchEvent) ? e.touches[0].clientX : e.clientX))
      const clientY = Math.floor((e instanceof MouseEvent) ? e.clientY : ((e instanceof TouchEvent) ? e.touches[0].clientY : e.clientY))
      if (uiStore.annotationInProgress) switch(tool?.type) {
        case 'marker':
          endAnnotating(clientX, clientY)
          break

        case 'box':
        case 'highlighter':
        case 'pencil':
          const interactionId = saveFreehandAnnotation()
          if (interactionId) {
            lastRecentlyAdded.current = {
              tCreated: Date.now(),
              interactionId
            }
            deferredEndAnnotating()
          }
          else {
            endAnnotating()
            click()
          }
      }
      else {
        click()
      }

      uiStore.setAnnotationInProgressData(null)
      uiStore.setSelectionStartRange(null)
      uiStore.setSelectionEndRange(null)
      uiStore.setPencilData(0, [])
    }

    const handleTouchStart = (e:TouchEvent) => {
      tool = sessionStore.getSelectionTool()
      if (!currentToolAllowed()) return false
      clickCoordinates.current = { x: Math.floor(e.touches[0].clientX), y: Math.floor(e.touches[0].clientY), t:Date.now() }
      if (
        (pointerDeviceRef.current === 'pen') ||
        (uiStore.annotationInProgress)
      ) {
        longPress(e)
      }
      else {
        startDetectLongPress(e)
      }
    }

    const handleTouchMove = (e:TouchEvent) => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current)
      trackAnnotation(e.touches[0].clientX, e.touches[0].clientY, e.touches[0].force)
    }

    const handleTouchEnd = (e:TouchEvent) => {
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current)
      const clientX = Math.floor(e.changedTouches[0].clientX)
      const clientY = Math.floor(e.changedTouches[0].clientY)

      handleClick({ clientX, clientY })

      const page = getPageFromCoordinate(storeId, clientX, clientY)
      if (!page) return
      if ((clientX > page.pageRect.left) && (clientX < page.pageRect.right) && (clientY > page.pageRect.top) && (clientY < page.pageRect.bottom)) {
        if  (e.cancelable) e.preventDefault()
      }
    }

    const handlePointer = (e:PointerEvent) => {
      pointerDeviceRef.current = e.pointerType
      // workaround for Firefox Mobile
      if ((e.pointerType === 'touch') && (e.width === 0)) pointerDeviceRef.current = 'pen'
    }

    // register event listener on mount
    const pageElement = document.getElementById(`${storeId}-pdf-viewer`)
    if (pageElement) {
      pageElement.addEventListener('mousedown', handleMouseDown, { signal, passive:true })
      pageElement.addEventListener('mousemove', handleMouseMove, { signal, passive:true })
      pageElement.addEventListener('mouseup', handleClick, { signal, passive:true })
      pageElement.addEventListener('touchstart', handleTouchStart, { signal, passive:false })
      pageElement.addEventListener('touchmove', handleTouchMove, { signal, passive:false })
      pageElement.addEventListener('touchend', handleTouchEnd, { signal, passive:false })
      pageElement.addEventListener('contextmenu', (e) => {
        e.preventDefault()
        e.stopPropagation();
        e.stopImmediatePropagation()
        return false
      }, { signal, passive:false })
      if (window.PointerEvent) pageElement.addEventListener('pointerdown', handlePointer, { signal, passive:true })
    }

    return () => {
      // unregister all event listener on unmount
      controller.abort()
      if (contiguousAnnotationIntervalRef.current) clearInterval(contiguousAnnotationIntervalRef.current)
      if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current)
      endAnnotating()
    }
  }, [storeId, interactionEditId, opStore, nodeId, pdfMetaStore, podId, podStore, sessionStore.session?.user.userId, sessionStore, uiStore])

  // this component only register and handle events
  return null
}

export default observer(PdfClickEvents)